import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type AppContext = {
  appId: string | null;
  appName: string | null;
};

interface AppState extends AppContext {
  setApp: (app: AppContext) => void;
  clearApp: () => void;
}

function readLegacyAppContext(): AppContext {
  if (typeof window === 'undefined') {
    return { appId: null, appName: null };
  }

  try {
    const appId = String(localStorage.getItem('dc.app_id') || '').trim();
    if (!appId || appId === 'farmer-portal') {
      return { appId: null, appName: null };
    }
    return { appId, appName: null };
  } catch {
    return { appId: null, appName: null };
  }
}

function syncLegacyAppContext(appId: string | null, appName: string | null) {
  if (typeof window === 'undefined') return;

  try {
    if (appId) localStorage.setItem('dc.app_id', appId);
    else localStorage.removeItem('dc.app_id');

    const rawAuth = localStorage.getItem('dc.auth');
    if (!rawAuth) return;

    const auth = JSON.parse(rawAuth);
    if (appId) {
      auth.app_id = appId;
      auth.appId = appId;
      if (appName) auth.appName = appName;
    } else {
      delete auth.app_id;
      delete auth.appId;
      delete auth.appName;
    }
    localStorage.setItem('dc.auth', JSON.stringify(auth));
  } catch {
    // ignore
  }
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      ...readLegacyAppContext(),
      setApp: ({ appId, appName }) => {
        const nextAppId = appId || null;
        const nextAppName = appName || null;
        set({ appId: nextAppId, appName: nextAppName });
        syncLegacyAppContext(nextAppId, nextAppName);
      },
      clearApp: () => {
        set({ appId: null, appName: null });
        syncLegacyAppContext(null, null);
      },
    }),
    {
      name: 'app-context',
      partialize: (s) => ({ appId: s.appId, appName: s.appName }),
    }
  )
);

